import { getDisplayDate } from 'utils/date';
import { abbrNum, getPercentage } from 'utils/helper/common';
import { renderPrice } from 'utils/helper/price';

import type {
  SortableTableSort
} from 'types';
import type {
  CcmCampaignReportingOutfitPerformanceAll
} from 'modules/campaigns/components/ccm/types/CcmCampaignReporting.type';

export const CCM_REPORTING_TYPE_BY_COUNTRY = 'country';
export const CCM_REPORTING_TYPE_BY_OUTFIT = 'outfit';

export const CCM_REPORTING_TYPES = [
  CCM_REPORTING_TYPE_BY_COUNTRY,
  CCM_REPORTING_TYPE_BY_OUTFIT
];

export const CCM_REPORTING_GO_LIVE_DATE = 'go_live_date';
export const CCM_REPORTING_TOTAL_BUDGET_SPENT = 'total_budget_spent';
export const CCM_REPORTING_LIVE_OUTFITS = 'live_outfits';
export const CCM_REPORTING_OUTFITS_VIEWS = 'outfits_views';
export const CCM_REPORTING_OUTFITS_CLICKS = 'outfits_clicks';
export const CCM_REPORTING_OUTFITS_CTR = 'outfits_ctr';
export const CCM_REPORTING_ATTRIBUTED_PDP_VIEWS = 'outfits_product_page_views';
export const CCM_REPORTING_ITEMS_SOLD = 'outfits_num_items_sold';
export const CCM_REPORTING_GMV = 'outfits_revenue';

export const CCM_REPORTING_METRICS_COLUMNS = [
  CCM_REPORTING_GO_LIVE_DATE,
  CCM_REPORTING_TOTAL_BUDGET_SPENT,
  CCM_REPORTING_LIVE_OUTFITS,
  CCM_REPORTING_OUTFITS_VIEWS,
  CCM_REPORTING_OUTFITS_CLICKS,
  CCM_REPORTING_OUTFITS_CTR,
  CCM_REPORTING_ATTRIBUTED_PDP_VIEWS,
  CCM_REPORTING_ITEMS_SOLD,
  CCM_REPORTING_GMV
];

export const CCM_REPORTING_METRICS_TO_OUTFIT_PERFORMANCE_MAP = {
  [CCM_REPORTING_GO_LIVE_DATE]: {
    propName: 'goLiveDate',
    formatter: getDisplayDate,
    defaultValue: '1970-01-01'
  },
  [CCM_REPORTING_TOTAL_BUDGET_SPENT]: {
    propName: 'totalBudgetSpent',
    formatter: renderPrice,
    defaultValue: 0
  },
  [CCM_REPORTING_LIVE_OUTFITS]: {
    propName: 'liveOutfits',
    formatter: abbrNum,
    defaultValue: 0
  },
  [CCM_REPORTING_OUTFITS_VIEWS]: {
    propName: 'outfitsViews',
    formatter: abbrNum,
    defaultValue: 0
  },
  [CCM_REPORTING_OUTFITS_CLICKS]: {
    propName: 'outfitsClicks',
    formatter: abbrNum,
    defaultValue: 0
  },
  [CCM_REPORTING_OUTFITS_CTR]: {
    propName: 'outfitsCtr',
    formatter: (value: number): string => `${getPercentage(value, 1)}%`,
    defaultValue: 0
  },
  [CCM_REPORTING_ATTRIBUTED_PDP_VIEWS]: {
    propName: 'outfitsProductPageViews',
    formatter: abbrNum,
    defaultValue: 0
  },
  [CCM_REPORTING_ITEMS_SOLD]: {
    propName: 'outfitsNumItemsSold',
    formatter: abbrNum,
    defaultValue: 0
  },
  [CCM_REPORTING_GMV]: {
    propName: 'outfitsRevenue',
    formatter: renderPrice,
    defaultValue: 0
  }
};

export const getSortingFunction = () => ({
  countryCode: (
    a: CcmCampaignReportingOutfitPerformanceAll,
    b: CcmCampaignReportingOutfitPerformanceAll
  ): number => (
    a.countryCode.localeCompare(b.countryCode)
  ),
  totalBudgetSpent: (
    a: CcmCampaignReportingOutfitPerformanceAll,
    b: CcmCampaignReportingOutfitPerformanceAll
  ): number => (
    a.totalBudgetSpent - b.totalBudgetSpent
  ),
  liveOutfits: (
    a: CcmCampaignReportingOutfitPerformanceAll,
    b: CcmCampaignReportingOutfitPerformanceAll
  ): number => (
    a.liveOutfits - b.liveOutfits
  ),
  outfitsViews: (
    a: CcmCampaignReportingOutfitPerformanceAll,
    b: CcmCampaignReportingOutfitPerformanceAll
  ): number => (
    a.outfitsViews - b.outfitsViews
  ),
  outfitsClicks: (
    a: CcmCampaignReportingOutfitPerformanceAll,
    b: CcmCampaignReportingOutfitPerformanceAll
  ): number => (
    a.outfitsClicks - b.outfitsClicks
  ),
  outfitsCtr: (
    a: CcmCampaignReportingOutfitPerformanceAll,
    b: CcmCampaignReportingOutfitPerformanceAll
  ): number => (
    a.outfitsCtr - b.outfitsCtr
  ),
  outfitsProductPageViews: (
    a: CcmCampaignReportingOutfitPerformanceAll,
    b: CcmCampaignReportingOutfitPerformanceAll
  ): number => (
    a.outfitsProductPageViews - b.outfitsProductPageViews
  ),
  outfitsNumItemsSold: (
    a: CcmCampaignReportingOutfitPerformanceAll,
    b: CcmCampaignReportingOutfitPerformanceAll
  ): number => (
    a.outfitsNumItemsSold - b.outfitsNumItemsSold
  ),
  outfitsRevenue: (
    a: CcmCampaignReportingOutfitPerformanceAll,
    b: CcmCampaignReportingOutfitPerformanceAll
  ): number => (
    a.outfitsRevenue - b.outfitsRevenue
  ),
  outfitDetails: (
    a: CcmCampaignReportingOutfitPerformanceAll,
    b: CcmCampaignReportingOutfitPerformanceAll
  ): number => (
    a.outfitDetails.outfitName.localeCompare(b.outfitDetails.outfitName)
  ),
  brandedSku: (
    a: CcmCampaignReportingOutfitPerformanceAll,
    b: CcmCampaignReportingOutfitPerformanceAll
  ): number => (
    a.brandedSku - b.brandedSku
  )
});

export const getSortedData = <T>(
  data: Array<T>,
  sortingOptions: SortableTableSort
): Array<T> => {
  const sortBy = getSortingFunction();

  return data.slice().sort((a, b) => {
    const order = sortBy[sortingOptions.sortingKey](a, b);
    return sortingOptions.ascending ? order : -order;
  });
};
